import React, { useMemo, useState } from 'react';
import { CalendarCheque, formatDMY } from './ChequeCalendar';
import { formatARS } from '../../utils/formatCurrency';

export type ListedCheque = CalendarCheque & { partyCuit?: string };

type PartyFilter = 'todos' | 'client' | 'supplier';
type StatusFilter = 'activos' | 'pagados' | 'rechazados' | 'todos';

const OBSERVATION: Record<string, string> = { pendiente: 'Pendiente', depositado: 'Depositado', cobrado: 'PAGADO', rechazado: 'RECHAZADO' };
const NO_DATE = '9999-12-31';

const isActive = (c: ListedCheque) => c.checkStatus !== 'cobrado' && c.checkStatus !== 'rechazado';
const localIso = (d = new Date()) => d.toLocaleDateString('sv-SE');
const signed = (c: ListedCheque) => (c.partyType === 'client' ? 1 : -1) * Number(c.amount || 0);

const ChequeList: React.FC<{ cheques: ListedCheque[] }> = ({ cheques }) => {
  const [party, setParty] = useState<PartyFilter>('todos');
  const [status, setStatus] = useState<StatusFilter>('todos');
  const todayIso = localIso();
  const [day, setDay] = useState(todayIso);

  // Running result from the first cheque up to each due date, paid or not; rejected ones never moved money.
  const timeline = useMemo(() => {
    const dated = cheques
      .filter((c) => c.checkStatus !== 'rechazado')
      .map((c) => {
        const due = String(c.checkDueDate || '').slice(0, 10);
        return { c, date: /^\d{4}-\d{2}-\d{2}$/.test(due) ? due : NO_DATE };
      })
      .sort((a, b) => a.date.localeCompare(b.date) || (a.c.partyType === b.c.partyType ? a.c.id - b.c.id : a.c.partyType === 'client' ? -1 : 1));
    let balance = 0;
    const byId = new Map<number, { date: string; balance: number }>();
    dated.forEach(({ c, date }) => { balance += signed(c); byId.set(c.id, { date, balance }); });
    return { dated, byId };
  }, [cheques]);

  const upToDay = timeline.dated.filter((p) => p.date <= day);
  const sumOf = (type: 'client' | 'supplier', paid?: boolean) => upToDay
    .filter((p) => p.c.partyType === type && (paid === undefined || (p.c.checkStatus === 'cobrado') === paid))
    .reduce((sum, p) => sum + Number(p.c.amount || 0), 0);
  const countOf = (type: 'client' | 'supplier') => upToDay.filter((p) => p.c.partyType === type).length;
  const dayInflow = sumOf('client');
  const dayOutflow = sumOf('supplier');
  const dayBalance = dayInflow - dayOutflow;
  const firstDate = timeline.dated[0]?.date;
  const shift = (days: number) => { const [y, m, d] = day.split('-').map(Number); setDay(localIso(new Date(y, m - 1, d + days))); };

  const rows = useMemo(() => cheques
    .filter((c) => party === 'todos' || c.partyType === party)
    .filter((c) => status === 'todos'
      || (status === 'activos' && isActive(c))
      || (status === 'pagados' && c.checkStatus === 'cobrado')
      || (status === 'rechazados' && c.checkStatus === 'rechazado'))
    .sort((a, b) => (a.checkDueDate || '9999').localeCompare(b.checkDueDate || '9999') || a.id - b.id), [cheques, party, status]);

  // Rejected cheques never moved money, so they stay out of the totals.
  const total = (type: 'client' | 'supplier', paid: boolean) => rows
    .filter((c) => c.partyType === type && c.checkStatus !== 'rechazado' && (c.checkStatus === 'cobrado') === paid)
    .reduce((sum, c) => sum + Number(c.amount || 0), 0);
  const summary = {
    client: { paid: total('client', true), pending: total('client', false) },
    supplier: { paid: total('supplier', true), pending: total('supplier', false) },
  };
  const result = { paid: summary.client.paid - summary.supplier.paid, pending: summary.client.pending - summary.supplier.pending };
  const hasPaid = summary.client.paid > 0 || summary.supplier.paid > 0;
  const hasPending = summary.client.pending > 0 || summary.supplier.pending > 0;

  const pill = (active: boolean) => `rounded-full border px-3 py-1 text-xs font-medium ${active ? 'border-primary-600 bg-primary-50 text-primary-700' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`;
  const dayButton = 'rounded-lg border border-blue-300 bg-white px-3 py-1.5 text-sm hover:bg-blue-50';

  return (
    <section className="space-y-4 rounded-xl border border-gray-200 bg-white p-3 shadow-sm sm:p-4">
      <div>
        <h2 className="font-semibold text-gray-900">Listado de cheques</h2>
        <p className="mt-1 text-xs text-gray-600">Cheques de clientes (entran) y cheques entregados a proveedores (salen), ordenados por fecha de pago.</p>
      </div>

      <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-3">
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs font-medium text-blue-900">¿Cómo estoy desde el principio hasta el día...?
            <input type="date" value={day} onChange={(e) => setDay(e.target.value || todayIso)} className="mt-1 block rounded-lg border border-blue-300 bg-white px-2 py-1.5 text-sm" />
          </label>
          <button type="button" onClick={() => shift(-1)} className={dayButton}>− 1 día</button>
          <button type="button" onClick={() => shift(1)} className={dayButton}>+ 1 día</button>
          <button type="button" onClick={() => shift(7)} className={dayButton}>+ 1 semana</button>
          {day !== todayIso && <button type="button" onClick={() => setDay(todayIso)} className="px-2 py-1.5 text-sm font-medium text-primary-600 hover:underline">Volver a hoy</button>}
        </div>
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
          <div className="rounded-lg border bg-white p-3"><p className="text-xs text-gray-500">Entra de clientes</p><p className="text-lg font-bold text-green-700">{formatARS(dayInflow)}</p><p className="text-[11px] text-gray-500">{countOf('client')} cheque{countOf('client') === 1 ? '' : 's'} · cobrado {formatARS(sumOf('client', true))} · por cobrar {formatARS(sumOf('client', false))}</p></div>
          <div className="rounded-lg border bg-white p-3"><p className="text-xs text-gray-500">Sale a proveedores</p><p className="text-lg font-bold text-red-600">{formatARS(dayOutflow)}</p><p className="text-[11px] text-gray-500">{countOf('supplier')} cheque{countOf('supplier') === 1 ? '' : 's'} · pagado {formatARS(sumOf('supplier', true))} · por pagar {formatARS(sumOf('supplier', false))}</p></div>
          <div className={`rounded-lg border p-3 ${dayBalance < 0 ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}`}><p className="text-xs text-gray-600">Resultado (entra − sale)</p><p className={`text-lg font-bold ${dayBalance < 0 ? 'text-red-700' : 'text-green-800'}`}>{formatARS(dayBalance)}</p><p className="text-[11px] text-gray-500">{dayBalance < 0 ? 'Pagaste más de lo que cobraste' : 'Cobraste más de lo que pagaste'}</p></div>
        </div>
        <p className="mt-2 text-[11px] text-blue-900">Suma todos los cheques {firstDate && firstDate !== NO_DATE ? `desde el primero (${formatDMY(firstDate)})` : 'desde el primero'} hasta el {formatDMY(day)} inclusive, cobrados o no, por fecha de pago. Los rechazados no cuentan. Tocá una fila para posicionarte en esa fecha.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={pill(party === 'todos')} onClick={() => setParty('todos')}>Clientes y proveedores</button>
        <button type="button" className={pill(party === 'client')} onClick={() => setParty('client')}>Solo clientes</button>
        <button type="button" className={pill(party === 'supplier')} onClick={() => setParty('supplier')}>Solo proveedores</button>
        <span className="mx-1 hidden h-5 w-px bg-gray-200 sm:inline-block" />
        <button type="button" className={pill(status === 'activos')} onClick={() => setStatus('activos')}>Sin acreditar</button>
        <button type="button" className={pill(status === 'pagados')} onClick={() => setStatus('pagados')}>Pagados</button>
        <button type="button" className={pill(status === 'rechazados')} onClick={() => setStatus('rechazados')}>Rechazados</button>
        <button type="button" className={pill(status === 'todos')} onClick={() => setStatus('todos')}>Todos</button>
      </div>

      <div className="overflow-x-auto rounded-lg border">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-3 sm:px-4">Cliente / Proveedor</th>
              <th className="px-3 py-3 sm:px-4">F. de pago</th>
              <th className="px-3 py-3 text-right sm:px-4">Importe</th>
              <th className="px-3 py-3 sm:px-4">Observaciones</th>
              <th className="px-3 py-3 text-right sm:px-4">Saldo acumulado</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map((c) => {
              const point = timeline.byId.get(c.id);
              const afterDay = !!point && point.date > day;
              const onDay = !!point && point.date === day;
              const background = c.checkStatus === 'cobrado' ? 'bg-yellow-100' : c.checkStatus === 'rechazado' ? 'bg-red-50' : onDay ? 'bg-blue-50' : '';
              return (
                <tr key={c.id} onClick={() => point && point.date !== NO_DATE && setDay(point.date)} className={`${background} ${point ? 'cursor-pointer hover:bg-blue-50' : ''} ${afterDay ? 'text-gray-400' : ''}`}>
                  <td className="px-3 py-2 sm:px-4">
                    <span className={`mr-2 rounded-full px-2 py-0.5 text-xs font-semibold ${c.partyType === 'client' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{c.partyType === 'client' ? 'Cliente' : 'Proveedor'}</span>
                    {c.partyName || '(sin nombre)'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 sm:px-4">{formatDMY(c.checkDueDate)}</td>
                  <td className={`whitespace-nowrap px-3 py-2 text-right font-semibold sm:px-4 ${afterDay ? '' : c.partyType === 'client' ? 'text-green-700' : 'text-red-600'}`}>{formatARS(c.amount)}</td>
                  <td className={`whitespace-nowrap px-3 py-2 sm:px-4 ${c.checkStatus === 'cobrado' || c.checkStatus === 'rechazado' ? 'font-bold' : afterDay ? '' : 'text-gray-600'}`}>{OBSERVATION[c.checkStatus] || 'Pendiente'}</td>
                  <td className={`whitespace-nowrap px-3 py-2 text-right font-semibold sm:px-4 ${!point ? 'text-gray-300' : point.balance < 0 ? 'text-red-700' : afterDay ? '' : 'text-gray-900'}`}>{point ? formatARS(point.balance) : '-'}</td>
                </tr>
              );
            })}
            {!rows.length && <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">No hay cheques para este filtro.</td></tr>}
          </tbody>
        </table>
      </div>
      {rows.length > 0 && (
        <div className="overflow-x-auto rounded-lg border">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left sm:px-4">Totales del filtro</th>
                {hasPaid && <th className="px-3 py-2 text-right sm:px-4">Ya acreditado</th>}
                {hasPending && <th className="px-3 py-2 text-right sm:px-4">Sin acreditar (lo que viene)</th>}
                {hasPaid && hasPending && <th className="px-3 py-2 text-right sm:px-4">Total</th>}
              </tr>
            </thead>
            <tbody className="divide-y font-semibold">
              {party !== 'supplier' && (
                <tr>
                  <td className="px-3 py-2 sm:px-4">Clientes (entra)</td>
                  {hasPaid && <td className="px-3 py-2 text-right text-green-700 sm:px-4">{formatARS(summary.client.paid)}</td>}
                  {hasPending && <td className="px-3 py-2 text-right text-green-700 sm:px-4">{formatARS(summary.client.pending)}</td>}
                  {hasPaid && hasPending && <td className="px-3 py-2 text-right text-green-700 sm:px-4">{formatARS(summary.client.paid + summary.client.pending)}</td>}
                </tr>
              )}
              {party !== 'client' && (
                <tr>
                  <td className="px-3 py-2 sm:px-4">Proveedores (sale)</td>
                  {hasPaid && <td className="px-3 py-2 text-right text-red-600 sm:px-4">{formatARS(summary.supplier.paid)}</td>}
                  {hasPending && <td className="px-3 py-2 text-right text-red-600 sm:px-4">{formatARS(summary.supplier.pending)}</td>}
                  {hasPaid && hasPending && <td className="px-3 py-2 text-right text-red-600 sm:px-4">{formatARS(summary.supplier.paid + summary.supplier.pending)}</td>}
                </tr>
              )}
              {party === 'todos' && (
                <tr className="bg-gray-50">
                  <td className="px-3 py-2 sm:px-4">Resultado (entra − sale)</td>
                  {hasPaid && <td className={`px-3 py-2 text-right sm:px-4 ${result.paid >= 0 ? 'text-blue-800' : 'text-red-700'}`}>{formatARS(result.paid)}</td>}
                  {hasPending && <td className={`px-3 py-2 text-right sm:px-4 ${result.pending >= 0 ? 'text-blue-800' : 'text-red-700'}`}>{formatARS(result.pending)}</td>}
                  {hasPaid && hasPending && <td className={`px-3 py-2 text-right sm:px-4 ${result.paid + result.pending >= 0 ? 'text-blue-800' : 'text-red-700'}`}>{formatARS(result.paid + result.pending)}</td>}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {party === 'todos' && hasPaid && (
        <p className="text-xs text-gray-600">"Ya acreditado" es lo que ya pasó por el banco: si es negativo, pagaste a proveedores con cheques más de lo que cobraste a clientes con cheques, y la diferencia salió de tu saldo u otros ingresos. Para saber si vas a estar en negativo, mirá "Sin acreditar" junto con el saldo real del banco.</p>
      )}
      <p className="text-xs text-gray-500">El saldo acumulado es lo que entró menos lo que salió, cheque por cheque desde el primero, en orden de fecha de pago (si el mismo día entra y sale, primero entra). Los rechazados no suman. Las filas en gris son posteriores al día elegido.</p>
    </section>
  );
};

export default ChequeList;
